import { ToNumber } from "./promotionConfigHelper";

/**
 * Calculates how many included-album slots are unlocked by the current paid albums.
 * The calculation respects the configured commercial block size and the real number
 * of albums available for the reservation or download code.
 *
 * @param {Object|null} item Cart item.
 * @returns {number} Available included-album slots.
 */
export function GetPromotionAvailableIncludedSlots(item = null) {
    if (!item) {
        return 0;
    }

    const maxAlbumsPerBatch = ToNumber(item?.PromotionMaxAlbumsPerBatch, 0);
    const requiredPurchases = GetPromotionRequiredPurchases(item);
    const paidQuantity = ToNumber(item?.PromotionPaidQuantity, 0);
    const availableAlbumsQuantity = ToNumber(
        item?.PromotionAvailableAlbumsQuantity || item?.AllAvailableParksLength,
        0
    );
    const maxFreeAlbums = ToNumber(item?.PromotionMaxFreeAlbums, 0);
    const includedSlotsPerBatch = Math.max(maxAlbumsPerBatch - requiredPurchases, 0);

    if (!maxAlbumsPerBatch || !requiredPurchases || !includedSlotsPerBatch) {
        return 0;
    }

    const paidBatches = Math.floor(paidQuantity / requiredPurchases);
    const slotsUnlockedByPaidAlbums = paidBatches * includedSlotsPerBatch;
    const realAvailableSlots = availableAlbumsQuantity > 0
        ? Math.max(availableAlbumsQuantity - paidQuantity, 0)
        : slotsUnlockedByPaidAlbums;
    const configuredFreeLimit = maxFreeAlbums > 0
        ? maxFreeAlbums
        : slotsUnlockedByPaidAlbums;

    return Math.max(
        Math.min(slotsUnlockedByPaidAlbums, realAvailableSlots, configuredFreeLimit),
        0
    );
}

/**
 * Gets how many included albums are still pending to be added manually.
 * The stored cart value is preferred when present; otherwise the helper recalculates it.
 *
 * @param {Object|null} item Cart item.
 * @returns {number} Pending included-album quantity.
 */
export function GetPromotionPendingRemaining(item = null) {
    if (!item) {
        return 0;
    }

    const storedRemaining = ToNumber(item?.PromotionPendingRemaining, 0);

    if (storedRemaining > 0) {
        return storedRemaining;
    }

    const includedQuantity = ToNumber(item?.PromotionIncludedQuantity, 0);
    const availableIncludedSlots = GetPromotionAvailableIncludedSlots(item);

    return Math.max(availableIncludedSlots - includedQuantity, 0);
}

/**
 * Gets the number of paid albums required to unlock the promotion.
 *
 * @param {Object|null} item Cart item.
 * @returns {number} Required paid-album quantity.
 */
export function GetPromotionRequiredPurchases(item = null) {
    return Math.max(
        ToNumber(item?.PromotionRequiredPurchases || item?.PromotionPaidQuantity, 0),
        0
    );
}

/**
 * Gets the maximum size of each commercial album block.
 *
 * @param {Object|null} item Cart item.
 * @returns {number} Maximum block size.
 */
export function GetPromotionMaxAlbumsPerBatch(item = null) {
    return Math.max(ToNumber(item?.PromotionMaxAlbumsPerBatch, 0), 0);
}

/**
 * Calculates how many included albums are available inside each commercial block.
 *
 * @param {Object|null} item Cart item.
 * @returns {number} Included slots per block.
 */
export function GetPromotionIncludedSlots(item = null) {
    const maxAlbumsPerBatch = GetPromotionMaxAlbumsPerBatch(item);
    const requiredPurchases = GetPromotionRequiredPurchases(item);

    return Math.max(maxAlbumsPerBatch - requiredPurchases, 0);
}

/**
 * Builds the i18n context for the short cart alert shown when there are pending
 * included albums. Text rendering is intentionally left to Vue components.
 *
 * @param {Object|null} item Cart item.
 * @returns {{key: string, choice: number, params: Object, remaining: number}} Translation context.
 */
export function GetPromotionPendingMessageContext(item = null) {
    const remaining = GetPromotionPendingRemaining(item);

    return {
        key: "messages.promotion_missing_albums",
        choice: remaining,
        params: { count: remaining },
        remaining,
    };
}

/**
 * Builds the i18n context for the promotion detail modal body. Text rendering is
 * intentionally left to Vue components so this helper stays language agnostic.
 *
 * @param {Object|null} item Cart item.
 * @returns {{key: string, params: Object}} Translation context.
 */
export function GetPromotionDetailsMessageContext(item = null) {
    const requiredPurchases = GetPromotionRequiredPurchases(item);
    const maxAlbumsPerBatch = GetPromotionMaxAlbumsPerBatch(item);
    const includedSlots = GetPromotionIncludedSlots(item);

    return {
        key: "messages.promotion_details_body",
        params: {
            required: requiredPurchases,
            included: includedSlots,
            max: maxAlbumsPerBatch,
        },
    };
}

export default {
    GetPromotionAvailableIncludedSlots,
    GetPromotionPendingRemaining,
    GetPromotionRequiredPurchases,
    GetPromotionMaxAlbumsPerBatch,
    GetPromotionIncludedSlots,
    GetPromotionPendingMessageContext,
    GetPromotionDetailsMessageContext,
};
